import { PaymentMethodBrand } from '@sparelabs/domain-logic'
import { DutyStatus } from './DutyTypes'
import { CreatedInterface } from './EstimateTypes'
import { FleetType } from './FleetTypes'
import { NativeObjectType } from './NativeObjectTypes'
import { RequestCancellationFault, RequestCancelledBy } from './RequestCancellationTypes'
import { RequestIntentType, RequestStatus } from './RequestTypes'
import { MatchingEndpoint } from './SharedTypes'
import { ISlotResponse } from './SlotTypes'
import { DayOfWeek } from './TimeTypes'
import { UserStatus } from './UserTypes'

interface IListOperationQueryParams {
  skip?: number
  limit?: number
}

export interface IRidershipExportQueryParams extends IListOperationQueryParams {
  serviceIds?: string[]
  fleetIds?: string[]
  riderIds?: string[]
  fromRequestedPickupTs?: number
  toRequestedPickupTs?: number
  days?: DayOfWeek[]
  includeUnmatchedRequests?: boolean
  dateTimeFormat?: string
}

/**
 * TODO this can be deprecated or removed once a File based API is implemented
 */
export interface IRidershipExportResponse {
  id: string
  createdAtTime: string

  originalRequestedPickupTime: string | null
  requestedPickupTime: string
  requestedPickupAddress: string
  requestedPickupLong: number
  requestedPickupLat: number

  originalRequestedDropoffTime: string | null
  requestedDropoffTime: string
  requestedDropoffAddress: string
  requestedDropoffLong: number
  requestedDropoffLat: number

  scheduledPickupTime: string | null
  scheduledPickupAddress: string | null
  scheduledPickupLong: number | null
  scheduledPickupLat: number | null

  scheduledDropoffTime: string | null
  scheduledDropoffAddress: string | null
  scheduledDropoffLong: number | null
  scheduledDropoffLat: number | null

  pickupOTPWindowStart: string | null
  pickupOTPWindowEnd: string | null
  dropoffOTPWindowStart: string | null
  dropoffOTPWindowEnd: string | null

  pickupWalkingDurationS: number
  dropoffWalkingDurationS: number

  riderId: string | null
  riderExternalNumericId: number | null
  riderName: string | null
  riderPhone: string | null
  riderEmail: string | null
  numRidersAdult: number
  numRidersInfant: number
  numRidersChild: number
  numRidersYouth: number
  numRidersSenior: number
  numRidersStudent: number
  numRidersDisabled: number
  numRidersVeteran: number
  numRidersReduced: number
  numRidersPersonalCareAttendant: number
  numRidersOther: number

  accessibilityStrollerCount: number
  accessibilityWheelchairCount: number
  accessibilityChildSeatBabyCount: number
  accessibilityChildSeatYoungCount: number
  accessibilityServiceAnimalCount: number
  accessibilityBikeCount: number
  accessibilityOversizedWheelchairCount: number
  accessibilityLiftCount: number
  accessibilityWalkerCount: number
  accessibilityWhiteCaneCount: number
  accessibilityActualDoorToDoorCount: number
  accessibilityStretcherCount: number
  accessibilityFoldingWheelchairCount: number
  accessibilityOnlyCarRiderCount: number
  accessibilityExtraSpaceAmbulantCount: number
  accessibilityOversizedItemCount: number
  accessibilityOxygenTankCount: number
  accessibilityPoweredWheelchairCount: number
  accessibilityOversizedScooterCount: number
  accessibilityScooterCount: number
  accessibilityOnlyCarWalkerCount: number
  accessibilityOversizedWalkerCount: number

  dutyId: string | null
  dutyIdentifier: string | null
  driverId: string | null
  driverName: string | null
  vehicleLicensePlate: string | null
  vehicleIdentifier: string | null
  vehicleTypeName: string | null

  serviceId: string | null
  serviceName: string | null
  fleetId: string | null
  fleetName: string | null
  fleetType: FleetType | null
  externalDispatchFleetId: string | null
  notes: string | null
  pickupNotes: string | null
  dropoffNotes: string | null

  createdInterface: CreatedInterface

  status: RequestStatus
  statusChangeArrivingTime: string | null
  statusChangeInProgressTime: string | null
  statusChangeCancelledTime: string | null
  statusChangeCompletedTime: string | null

  pickupArrivedTime: string | null
  dropoffArrivedTime: string | null
  pickupLatenessSeconds: number
  dropoffLatenessSeconds: number
  relevantLatenessSeconds: number

  pickupZoneName: string | null
  dropoffZoneName: string | null

  pickupGroupName: string | null
  pickupGroupMembershipId: string | null
  pickupGroupMembershipStartTime: string | null
  pickupGroupMembershipExpiryTime: string | null
  pickupGroupMembershipMetadata: Record<string, unknown> | null

  dropoffGroupName: string | null
  dropoffGroupMembershipId: string | null
  dropoffGroupMembershipStartTime: string | null
  dropoffGroupMembershipExpiryTime: string | null
  dropoffGroupMembershipMetadata: Record<string, unknown> | null

  pickupCompletedDistanceDeviationM: number | null
  dropoffCompletedDistanceDeviationM: number | null

  cancellationCancelledBy: RequestCancelledBy | null
  cancellationFault: RequestCancellationFault | null
  cancellationReason: string | null
  cancellationNotes: string | null

  estimateCreatedAtTime: string
  estimateIntentType: RequestIntentType
  estimateEndpoint: MatchingEndpoint

  fareCost: number
  fareDiscount: number

  tip: number
  tipCreatedTime: string | null

  paymentMethodId: string | null
  paymentMethodType: string | null
  paymentMethodBrand: PaymentMethodBrand | null
  paymentMethodLast4Digits: string | null

  riderRating: number | null
  riderReviewTags: string[]
  riderComment: string | null

  promoNames: string[]
  promoDiscounts: number[]

  farePassNames: string[]
  farePassDiscounts: number[]

  chargeExternalIds: string[] | null

  inVehicleTripDistanceM: number | null

  tripDurationSeconds: number | null
  tripWaitSeconds: number | null

  estimatedTravelDistanceM: number
  estimatedTravelDuration: number

  riderMetadata: Record<string, any>
  driverMetadata: Record<string, any>

  createdBy: string | null

  driverLocationOnMatchLong: number | null
  driverLocationOnMatchLat: number | null
  acceptedTime: string | null
  deadheadToPickupM: number | null

  recurrenceId: string | null

  originalScheduledPickupTime: string | null
  originalScheduledDropoffTime: string | null

  requestMetadata: Record<string, unknown>

  fixedRouteTravelDuration: number | null
}

export interface IDriverMetricReportExportQueryParams extends IListOperationQueryParams {
  fleetIds?: string[]
  fromStartRequestedTs?: number
  toStartRequestedTs?: number
  days?: DayOfWeek[]
  dateTimeFormat?: string
}

export interface IDriverMetricReportExportResponse {
  driverId: string
  driverName: string | null
  createdAtTime: string
  lastSeenTime: string | null
  status: UserStatus
  rating: number
  totalDuties: number
  scheduledVehicleS: number
  actualVehicleS: number
  revenueHours: number
  totalTravelDistanceM: number
  deadheadTravelDistanceM: number
  totalRevenueDistanceM: number
  totalBoardings: number
  totalRequests: number
  totalFareCollected: number
  totalLatePullouts: number
  totalPulloutLatenessMinutes: number
  totalPulloutsOverTenMinutesLate: number
  averagePulloutLatenessMinutes: number
  totalDutyDelays: number
  totalDutyDelayMinutes: number
  totalDutyDelaysOverTenMinutes: number
  averageDutyDelayMinutes: number
  driverMetadata: Record<string, any>
}

export interface IDriverReportExportQueryParams extends IListOperationQueryParams {
  fleetIds?: string[]
  fromStartRequestedTs?: number
  toStartRequestedTs?: number
  days?: DayOfWeek[]
  dateTimeFormat?: string
}

export type IDriverReportExportBreakDetails = Pick<
  ISlotResponse,
  'scheduledTs' | 'startedTs' | 'completedTs' | 'cancelledTs'
>

/**
 * TODO this can be deprecated once a File based API is implemented
 */
export interface IDriverReportExportResponse {
  dutyId: string
  dutyIdentifier: string | null
  createdAtTime: string
  driverName: string | null
  vehicleIdentifier: string
  vehicleLicensePlate: string
  vehicleTypeName: string | null
  fleetName: string
  fleetType: FleetType
  startRequestedTime: string
  endRequestedTime: string | null
  startCompletedTime: string | null
  endCompletedTime: string | null
  cancelledTime: string | null
  firstSeenInServiceAreaTime: string | null
  lastSeenInServiceAreaTime: string | null
  totalDurationHours: number | null
  totalScheduledHours: number | null
  totalTravelDistanceM: number
  deadheadTravelDistanceM: number
  totalDurationNoRidersOnboardS: number | null
  totalNumberOfBreaks: number
  totalScheduledBreakLengthS: number
  totalBreakLengthS: number
  totalInterruptionLengthS: number
  totalNumberOfInterruptions: number
  totalBreakDistanceM: number
  totalBoardings: number
  riderInterfaceBoardings: number
  riderWebInterfaceBoardings: number
  ivrInterfaceBoardings: number
  aiVoiceInterfaceBoardings: number
  aiChatInterfaceBoardings: number
  openBookingInterfaceBoardings: number
  driverInterfaceBoardings: number
  adminInterfaceBoardings: number
  status: DutyStatus
  totalRevenueDistanceM: number
  totalRevenueHours: number
  totalInProgressPauseLengthS: number
  driverMetadata: Record<string, any>
  breaks: IDriverReportExportBreakDetails[]
  dutyMetadata: Record<string, unknown>
  fleetMetadata: Record<string, unknown>
}

export interface INTDExportQueryParams {
  startDate: string
  endDate: string
  fleetIds?: string[]
  serviceIds?: string[]
  days?: DayOfWeek[]
}

export interface ISummaryNTDExportQueryParams {
  startDate: string
  endDate: string
  days?: DayOfWeek[]
  fleetIds?: string[]
  serviceIds?: string[]
}

/**
 * TODO this can be deprecated once a File based API is implemented
 */
export interface INTDExportResponse extends INTDDutyMetrics, INTDRequestMetrics {
  date: string
}

export enum SummaryNTDExportFieldTypes {
  activeDutyCountMax = 'activeDutyCountMax',
  deadheadDistanceM = 'deadheadDistanceM',
  vehiclesInOperation = 'vehiclesInOperation',
  totalActualVehicleDistanceM = 'totalActualVehicleDistanceM',
  totalActualVehicleRevenueDistanceM = 'totalActualVehicleRevenueDistanceM',
  totalScheduledVehicleRevenueDistanceM = 'totalScheduledVehicleRevenueDistanceM',
  totalActualVehicleHourS = 'totalActualVehicleHourS',
  totalActualVehicleRevenueHourS = 'totalActualVehicleRevenueHourS',
  deadheadHourS = 'deadheadHourS',
  passengerDistanceTraveledM = 'passengerDistanceTraveledM',
  unlinkedPassengerTrips = 'unlinkedPassengerTrips',
}

export interface ISummaryNTDMetrics {
  activeDutyCountMax: number
  deadheadDistanceM: number
  vehiclesInOperation: number
  totalActualVehicleDistanceM: number
  totalActualVehicleRevenueDistanceM: number
  totalScheduledVehicleRevenueDistanceM: number
  totalActualVehicleHourS: number
  totalActualVehicleRevenueHourS: number
  deadheadHourS: number
  passengerDistanceTraveledM: number
  unlinkedPassengerTrips: number
}

export interface ISummaryNTDExportResponseAverage {
  field: SummaryNTDExportFieldTypes
  averageWeekdaySchedule: number
  averageSaturdaySchedule: number
  averageSundaySchedule: number
  total: number
}

export interface ISummaryNTDExportResponseDays extends ISummaryNTDMetrics {
  date: string
}

export type SummaryNTDExportResponse = ISummaryNTDExportResponseAverage | ISummaryNTDExportResponseDays

export interface INTDDutyMetrics {
  scheduledVehicleHourS: number
  actualVehicleHourS: number
  actualVehicleDistanceM: number
  revenueHourS: number
  revenueDistanceM: number
  totalBreakLengthS: number
  totalBreakDistanceM: number
  passengerDistanceTraveledM: number
}

export interface INTDRequestMetrics {
  numCompletedBoardings: number
  numCompletedRequests: number
  numCancelledRequests: number
  numNoShowRequests: number
}

export interface IDataRetentionReportExportQueryParams {
  fromTs: number
  scheduleIds: string[]
}

export interface IDataRetentionReportExportResponse {
  target: NativeObjectType
  id: string
  triggerDate: string
  retainUntilDate: string
  identifier: string | undefined
}
